import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Select, Space, Table } from "antd";
import { DownloadOutlined, EyeFilled, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import { downloadRestHoursPDF } from "../../../services/services";
import { ErrorToast, SuccessToast } from "../../../widgets/AppToast";

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: dayjs().month(index).format("MMMM"),
}));

const yearOptions = Array.from({ length: 8 }, (_, index) => {
  const year = dayjs().year() - 5 + index;
  return { value: year, label: year };
});

const getCrewId = (record) => record?.staff_id || record?.id;

const getCrewName = (record) =>
  record?.staff_name ||
  record?.name ||
  [record?.first_name, record?.last_name].filter(Boolean).join(" ") ||
  "N/A";

const getRank = (record) =>
  record?.staff_role ||
  record?.rank ||
  record?.role ||
  record?.designation ||
  record?.rank_name ||
  "N/A";

const getDepartment = (record) =>
  record?.department ||
  record?.department_name ||
  record?.staff_department ||
  "N/A";

const RestHoursCrewRecordsCom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preservedFilters = location.state?.filters || location.state || {};

  const [ships, setShips] = useState([]);
  const [shipId, setShipId] = useState(preservedFilters.ship_id || null);
  const [month, setMonth] = useState(
    preservedFilters.month || dayjs().month() + 1,
  );
  const [year, setYear] = useState(preservedFilters.year || dayjs().year());
  const [crew, setCrew] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
  });

  const currentFilters = useMemo(
    () => ({ ship_id: shipId, month, year }),
    [shipId, month, year],
  );

  useEffect(() => {
    const fetchShips = async () => {
      try {
        const response = await post(ApiURL.list_ships_view_com, new FormData());
        if (response?.status === true) {
          const list = response.ships || [];
          setShips(list);
          if (!shipId && list.length > 0) {
            const defaultShipId = preservedFilters.ship_id || list[0].id;
            setShipId(defaultShipId);
          }
        } else {
          ErrorToast("Error", response?.message || "Unable to load ships.");
        }
      } catch (error) {
        ErrorToast("Error", "Unexpected error while loading ships.");
      }
    };

    fetchShips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shipId) return;
    fetchCrew(shipId, month, year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipId, month, year]);

  const fetchCrew = async (
    selectedShipId,
    selectedMonth,
    selectedYear,
    page = 1,
  ) => {
    setLoading(true);
    setSelectedCrewIds([]);
    try {
      const formData = new FormData();
      formData.append("ship_id", selectedShipId);
      formData.append("month", selectedMonth);
      formData.append("year", selectedYear);
      formData.append("page", page);

      const response = await post(ApiURL.list_register_staff, formData);
      if (response.status) {
        setCrew(response.staff || []);

        setPagination({
          total: response.pagination.total,
          per_page: response.pagination.per_page,
        });
      } else {
        setCrew([]);
        ErrorToast("Error", response?.message || "Crew list failed.");
      }
    } catch (error) {
      setCrew([]);
      ErrorToast("Error", "Unexpected error while loading crew list.");
    } finally {
      setLoading(false);
    }
  };

  const navigateToLog = (record) => {
    navigate("/log-hours", {
      state: {
        source_panel: "company",
        staff_id: getCrewId(record),
        ship_id: shipId,
        month,
        year,
        returnTo: "/resthours/crew-records",
        filters: currentFilters,
      },
    });
  };

  const navigateToRecord = (record) => {
    navigate("/resthours/records", {
      state: {
        source_panel: "company",
        staff_id: getCrewId(record),
        ship_id: shipId,
        month,
        year,
        returnTo: "/resthours/crew-records",
        filters: currentFilters,
      },
    });
  };

  const downloadPDF = async () => {
    if (!selectedCrewIds.length || !shipId) return;

    setDownloading(true);
    try {
      const { blob, filename } = await downloadRestHoursPDF({
        ship_id: shipId,
        month,
        year,
        staff_ids: selectedCrewIds,
      });

      const fileName =
        filename ||
        `Rest_Hours_${dayjs()
          .month(month - 1)
          .format("MMMM")}_${year}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      SuccessToast("Success", "Rest hours PDF downloaded successfully.");
    } catch (error) {
      ErrorToast("Error", "PDF generation failed.");
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      title: "Staff Name",
      key: "staff_name",
      render: (_, record) => getCrewName(record),
    },
    {
      title: "Rank",
      key: "rank",
      render: (_, record) => getRank(record),
    },
    {
      title: "Department",
      key: "department",
      render: (_, record) => getDepartment(record),
    },
    {
      title: "Nationality",
      key: "nationality",
      align: "center",
      render: (_, record) => record?.nationality,
    },
    {
      title: "Sign-On Date",
      key: "sign_on_date",
      align: "center",
      render: (_, record) => record?.sign_on_date,
    },
    {
      title: "Sign-Off Date",
      key: "sign_off_date",
      align: "center",
      render: (_, record) =>
        record?.actual_sign_off_date || record?.expected_sign_off_date,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {/* <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => navigateToLog(record)}
          >
            Add Log
          </Button> */}
          <Button
            size="small"
            icon={<EyeFilled />}
            onClick={() => navigateToRecord(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        style={{
          borderRadius: 14,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontWeight: 700,
                color: "#1f2937",
              }}
            >
              Crew Records
            </h1>

            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
                marginTop: 3,
                fontWeight: 500,
              }}
            >
              View, manage and export crew work/rest hour records.
            </div>
          </div>

          <Space size={14} wrap>
            <div>
              {/* <h4 className="block-label">Ship</h4> */}
              <Select
                value={shipId}
                style={{ width: 210, height: 39 }}
                onChange={setShipId}
              >
                {ships.map((ship) => (
                  <Select.Option key={ship.id} value={ship.id}>
                    {ship.vessel_name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              {/* <div className="block-label">Month</div> */}
              <Select
                value={month}
                style={{ width: 210, height: 39 }}
                onChange={setMonth}
              >
                {monthOptions.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              {/* <div className="block-label">Year</div> */}
              <Select
                value={year}
                style={{ width: 210, height: 39 }}
                onChange={setYear}
              >
                {yearOptions.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              loading={downloading}
              disabled={!selectedCrewIds.length}
              onClick={downloadPDF}
            >
              Download PDF
            </Button>
          </Space>
        </div>
      </Card>

      <Table
        bordered
        className="custom-table"
        columns={columns}
        dataSource={crew}
        rowKey={(record) => getCrewId(record)}
        loading={loading || downloading}
        rowSelection={{
          selectedRowKeys: selectedCrewIds,
          onChange: setSelectedCrewIds,
          preserveSelectedRowKeys: false,
        }}
        locale={{ emptyText: <Empty description="No crew available" /> }}
        pagination={{
          current: currentPage,
          total: pagination.total,
          pageSize: pagination.per_page,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} crew`,
          onChange: (page) => {
            setCurrentPage(page);
            fetchCrew(shipId, month, year, page);
          },
        }}
        scroll={{ x: 700 }}
      />
    </>
  );
};

export default RestHoursCrewRecordsCom;
